import { sprintf }           from "../utils/FormatUtils";
import {channelData} from "../worker/DataWorker.ts";
import {CloneTransform} from "../layout/LayoutManager.ts";


export class Dynamic {
    row: number | null;
    key_channel: number;
    channel: number;
    dynamic_channel?: number;
    dynamic_key_channel?: number;
    table_name: string;
    field?: Field;
    dynamic_fields?: DynamicField[];
    hide_when_no_match: boolean;
    found: boolean;
    original_object: Dynamic;
    adopt_object: AdoptObject | null;
    is_dynamic: boolean;
    omit_duplicate: boolean;


    constructor(dynamic: any, transform?: CloneTransform) {
        this.original_object = Object.assign({}, dynamic);
        this.row = dynamic.row == null ? null : Number(dynamic.row);
        this.channel = dynamic.channel;
        this.key_channel = dynamic.key_channel;
        this.dynamic_channel = dynamic.dynamic_channel != 0 ? dynamic.dynamic_channel : null;
        this.dynamic_key_channel = dynamic.dynamic_key_channel != 0 ? dynamic.dynamic_key_channel : null;
        if (dynamic.field)
            this.field = new Field(dynamic.field.field, dynamic.field.type, dynamic.field.field_name);
        this.table_name = dynamic.table_name;
        this.hide_when_no_match = dynamic.hide_when_no_match || false;
        this.found = dynamic.found || false;
        this.is_dynamic = dynamic.is_dynamic || false;
        this.adopt_object = dynamic.adopt_object != null ? new AdoptObject(dynamic.adopt_object.adopt_object_id,
                dynamic.adopt_object.use_raw_data)
            : null;

        this.omit_duplicate = dynamic.omit_duplicate ?? false;

        if (dynamic.dynamic_fields) {
            this.dynamic_fields = [];
            for (const dynamicQuery of dynamic.dynamic_fields) {
                if (!dynamicQuery) continue;
                let source_field = undefined;
                let dynamic_field = undefined;
                let adopt_object = undefined;

                if (dynamicQuery.source_field) {
                    source_field = new Field
                    (dynamicQuery.source_field.field,
                        dynamicQuery.source_field.type,
                        dynamicQuery.source_field.field_name);
                }
                if (dynamicQuery.adopt_object) {
                    adopt_object = new AdoptObject(
                        dynamicQuery.adopt_object.adopt_object_id,
                        dynamicQuery.adopt_object.use_raw_data
                    );
                }
                dynamic_field = new Field(dynamicQuery.dynamic_field.field,
                    dynamicQuery.dynamic_field.type,
                    dynamicQuery.dynamic_field.field_name);
                this.dynamic_fields.push(new DynamicField(
                    source_field,
                    dynamic_field,
                    dynamicQuery.format || "",
                    adopt_object
                ));
            }
        }

        if (transform) {
            this.applyTransform(transform);
        }
    }

    public applyTransform(transforms: CloneTransform) {
        for (let transform of transforms.channel_map || []) {
            const { source, dest } = transform;
            if (this.key_channel == Number(source)) {
                this.key_channel = Number(dest);
            } else if (this.key_channel == null && this.channel == Number(source)) {
                this.channel = Number(dest);
            }
            if (this.dynamic_key_channel == Number(source)) {
                this.dynamic_key_channel = Number(dest);
            } else if (this.dynamic_key_channel == null && this.dynamic_channel == Number(source)) {
                this.dynamic_channel = Number(dest);
            }
        }
    }

    /* see docs/EXTRACTED_COMMENTS.md#c788 */
    public json() {
        let staticBody = staticJsonBodies.get(this);
        if (staticBody === undefined) {
            staticBody = JSON.stringify({
                table_name: this.table_name,
                field: this.field,
                dynamic_fields: this.dynamic_fields,
                hide_when_no_match: this.hide_when_no_match,
                is_dynamic: this.is_dynamic,
                adopt_object: this.adopt_object,
            }).slice(1, -1);
            staticJsonBodies.set(this, staticBody);
        }
        const head: string[] = [];
        const put = (key: string, value: unknown) => {
            if (value === undefined) return;
            head.push(`"${key}":${JSON.stringify(value)}`);
        };
        put("row", this.row);
        put("key_channel", this.key_channel);
        put("channel", this.channel);
        put("dynamic_channel", this.dynamic_channel);
        put("dynamic_key_channel", this.dynamic_key_channel);
        put("found", this.found);
        const glue = head.length > 0 && staticBody.length > 0 ? "," : "";
        return `{${head.join(",")}${glue}${staticBody}}`;
    }
}

/* see docs/EXTRACTED_COMMENTS.md#c788 */
const staticJsonBodies = new WeakMap<Dynamic, string>();

export class AdoptObject {
    adopt_object_id: string;
    use_raw_data: boolean;

    constructor(adopt_object_id: string, use_raw_data: boolean) {
        this.adopt_object_id = adopt_object_id;
        this.use_raw_data = use_raw_data ?? false;
    }
}


export class Field {
    field: number;
    type: number;
    fieldName: string;

    constructor(field: number, type: number, fieldName: string) {
        this.field = field;
        this.type = type;
        this.fieldName = fieldName;
    }
}

export class DynamicField {
    source_field?: Field;
    dynamic_field: Field;
    format: string;
    adopt_object?: AdoptObject;

    constructor(source_field: Field | undefined, dynamic_field: Field, format: string,
                adopt_object: AdoptObject | undefined) {
        this.source_field = source_field;
        this.dynamic_field = dynamic_field;
        this.format = format ?? null;
        this.adopt_object = adopt_object;
    }
}

export type DataField = {
    field: number;
    field_name: string;
    type: number;
    value: string;
}

export type DataRow = Map<number, DataField>;

export type Row = {
    data: DataField[];
    row: number;
    channel: number
};

/* see docs/EXTRACTED_COMMENTS.md#c010 */
function getField(row: DataRow | undefined, id: number): DataField | undefined {
    if (!row) return undefined;
    if (!row.has(id) || row.get(id)!.value === null || row.get(id)!.value == "") {
        return undefined;
    }
    return row.get(id);
}

/* ---------------------------------------------------------------- *\
 *  DynamicParser
\* ---------------------------------------------------------------- */
export class DynamicParser {
    private dynamic    : Dynamic;
    private rows       : Map<number, DataRow[]>;   // reference to channelData
    private adopt_data : Map<string, any>;
    private sourceCache = new Map<number, string | null>(); // memoised values

    constructor(
        dynamic         : Dynamic,
        rowsRef         : Map<number, DataRow[]> = channelData,  // default = shared graph
        adoptDataIter   : Iterable<readonly [string, any]> = []
    ) {
        this.dynamic    = dynamic;
        this.rows       = rowsRef;                     // NO copying
        this.adopt_data = Object.values(adoptDataIter).length
            ? new Map(adoptDataIter)
            : new Map();
    }

    /* ------------- accessors ------------------------------------- */

    private getRows(channelId: number): DataRow[] | null {
        return this.rows.get(channelId) ?? null;
    }

    private getChannel(): number | null {
        if (this.dynamic.key_channel && this.rows.has(this.dynamic.key_channel))
            return this.dynamic.key_channel;
        if (this.dynamic.channel     && this.rows.has(this.dynamic.channel))
            return this.dynamic.channel;
        return null;
    }

    private getDynamicChannel(): number | null {
        if (this.dynamic.dynamic_key_channel != null &&
            this.rows.has(this.dynamic.dynamic_key_channel))
            return this.dynamic.dynamic_key_channel;

        if (this.dynamic.dynamic_channel != null &&
            this.rows.has(this.dynamic.dynamic_channel))
            return this.dynamic.dynamic_channel;
        return null;
    }

    /* ------------- public API ------------------------------------ */
    public getRow(): [number, number] | null {
        /* dynamic-channel path */
        if (this.dynamic.is_dynamic) {
            const dynVal = this.getDynamicValue();
            if (dynVal != null) {
                const [rowId, ch] = dynVal;
                this.dynamic.found = true;
                return [rowId, ch];
            } else {
                this.dynamic.found = false;
            }
        }

        /* AdoptObject path */
        if (this.dynamic.adopt_object) {
            const adopt = this.dynamic.adopt_object;
            const obj   = this.adopt_data.get(adopt.adopt_object_id);
            return obj ? [obj.row, obj.channel] : null;
        }

        /* fixed row path */
        const ch = this.getChannel();
        if (ch == null)              {
            return null;
        }
        if (this.dynamic.row == null){
            return null;
        }
        return [this.dynamic.row, ch];
    }

    public getValue(rowObj: [number, number] | null): [DataRow, number] | null {
        /* AdoptObject path (fast) */
        if (this.dynamic.adopt_object) {
            const adopt = this.dynamic.adopt_object;
            const obj   = this.adopt_data.get(adopt.adopt_object_id);
            if (obj?.data == null) return null;
            let entries = Object.entries(obj.data!)
                .map(([key, value]) => [Number(key), value as DataField]);
            let data = new Map<number, DataField>(entries as [number, DataField][]);
            return obj ? [data!, obj.row] : null
        }

        if (!rowObj) return null;
        let [rowId, rowCh] = rowObj;
        const dynCh = this.getDynamicChannel();

        // Only a row resolved on the dynamic channel indexes it. A failed match falls back to
        // [row, channel], and that row number means nothing on the dynamic channel.
        if (dynCh != null && rowCh === dynCh) {
            const rows = this.getRows(dynCh);
            if (rows && rows[rowId]) {
                if (!this.dynamic.field || getField(rows[rowId], this.dynamic.field.field)) {
                    return [rows[rowId], dynCh];
                }
            }
        }

        if (!this.dynamic.is_dynamic) this.dynamic.row = rowId;
        if (this.dynamic.row == null) return null;
        rowId = this.dynamic.row;

        const keyRows = this.getRows(this.dynamic.key_channel);
        const datRows = this.getRows(this.dynamic.channel);

        if (!keyRows && !datRows) return null;

        if (!this.dynamic.field) {
            if (keyRows && keyRows[rowId]) return [keyRows[rowId], this.dynamic.key_channel];
            if (datRows && datRows[rowId]) return [datRows[rowId], this.dynamic.channel];
            return null;
        }

        const fid = this.dynamic.field.field;
        if (keyRows && keyRows[rowId] && getField(keyRows[rowId], fid))
            return [keyRows[rowId], this.dynamic.key_channel];
        if (datRows && datRows[rowId] && getField(datRows[rowId], fid)) {
            return [datRows[rowId], this.dynamic.channel];
        }
        return null;
    }

    public isFound() { return this.dynamic.found; }

    /* ------------- dynamic‑channel resolver ---------------------- */

    private getDynamicValue(): [number, number] | null {
        const dynCh = this.getDynamicChannel();
        if (dynCh == null) return null;

        const channelRows = this.getRows(dynCh);
        if (!channelRows?.length) return null;
        /* build candidate set once, intersect in place */
        const candidates = new Set<number>();
        channelRows.forEach((_r, idx) => candidates.add(idx));

        for (const df of this.dynamic.dynamic_fields ?? []) {
            let sourceVal: string | null = "";

            if (df.format == null || df.format == "" || df.format.includes("%")) {
                sourceVal = this.getSourceValue(df);
            }else {
                sourceVal = df.format;
            }

            if (sourceVal == null || sourceVal === "") return null;

            const wantedField = df.dynamic_field.field;

            for (const rowId of Array.from(candidates)) {
                const rowArr = channelRows[rowId];
                const val = getField(rowArr, wantedField)?.value;
                if (val !== sourceVal) candidates.delete(rowId);
            }

            if (!candidates.size) return null;
        }

        const first = candidates.values().next().value as number | undefined;
        return first === undefined ? null : [first, dynCh];
    }

    /* ------------- dynamic‑field helpers ------------------------- */

    private getSourceValue(df: DynamicField): string | null {
        /* AdoptObject path (fast) */
        if (df.adopt_object) {
            const adopt = df.adopt_object;
            const obj   = this.adopt_data.get(adopt.adopt_object_id);
            return obj ? (adopt.use_raw_data ? obj.raw : obj.format) : null;
        }

        /* source_field path (may recurse) */
        if (!df.source_field) return df.format;
        const fid = df.source_field.field;
        if (this.sourceCache.has(fid)) return this.sourceCache.get(fid)!;
        let val = this.computeSourceValue(df, fid);
        if (val == null) {
            return df.format;
        }
        this.sourceCache.set(fid, val);
        return val;
    }

    private computeSourceValue(df: DynamicField, fid: number): string | null {
        const format = df.format || "%s";

        /* recurse with a lightweight parser sharing the same rows */
        const parser = new DynamicParser(
            new Dynamic({
                field       : df.source_field,
                key_channel : this.dynamic.key_channel,
                channel     : this.dynamic.channel,
                row         : this.dynamic.row == null ? 0 : this.dynamic.row,
            }),
            this.rows,
            this.adopt_data.entries()
        );

        const res = parser.getValue(parser.getRow());
        if (!res) return null;
        const [rowArr] = res;
        const fieldVal = getField(rowArr, fid)?.value;

        if (fieldVal == null) return df.format;
        // see docs/EXTRACTED_COMMENTS.md#c011
        if (format === "%s") return String(fieldVal);
        return sprintf(format, fieldVal, df.source_field?.type);
    }
}
